import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Trophy, Users, Award, Sparkles } from 'lucide-react';
import api from '../lib/api';

/**
 * Event Performance Comparison dialog.
 *
 * Step 1 — user multi-selects 2–4 events (active or archived).
 * Step 2 — backend returns per-event roll-up plus cross-event "common ground"
 *   (shared customers, shared top designs) which are highlighted inline.
 */
const EventComparisonDialog = ({ open, onOpenChange }) => {
  const [step, setStep] = useState('pick'); // 'pick' | 'view'
  const [allEvents, setAllEvents] = useState([]);
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!open) {
      setStep('pick'); setSelected([]); setResult(null); return;
    }
    (async () => {
      try {
        const res = await api.get('/events?include_archived=true');
        setAllEvents(res.data);
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Failed to load events');
      }
    })();
  }, [open]);

  const toggle = (id) => {
    if (selected.includes(id)) setSelected(selected.filter(x => x !== id));
    else if (selected.length < 4) setSelected([...selected, id]);
    else toast.info('Maximum 4 events at once');
  };

  const runCompare = async () => {
    setLoading(true);
    try {
      const res = await api.post('/events/compare', { event_ids: selected });
      setResult(res.data);
      setStep('view');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Comparison failed');
    } finally { setLoading(false); }
  };

  const fmt = (n) => (n || 0).toLocaleString('en-IN');
  const isWinner = (evId, metric) => result?.winners?.[metric] === evId;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl max-h-[92vh] overflow-hidden flex flex-col" data-testid="event-compare-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Trophy className="w-5 h-5 text-amber-500" />
            Event Performance Comparison
          </DialogTitle>
          <DialogDescription>
            {step === 'pick'
              ? 'Pick 2–4 events to compare. We will highlight winners per metric and surface customers / designs common to all selected events.'
              : 'Side-by-side scoreboard. Winners are crowned per metric; shared customers and designs are pinned at the bottom.'}
          </DialogDescription>
        </DialogHeader>

        {step === 'pick' && (
          <div className="flex-1 overflow-auto">
            {allEvents.length === 0 && <p className="text-sm text-gray-400 py-12 text-center">No events available.</p>}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2" data-testid="event-compare-picker">
              {allEvents.map(ev => (
                <button
                  key={ev.id}
                  className={`text-left p-3 border rounded-lg transition-colors ${selected.includes(ev.id) ? 'border-pink-500 bg-pink-50 shadow-sm' : 'border-gray-200 hover:border-pink-300 hover:bg-pink-50/40'}`}
                  onClick={() => toggle(ev.id)}
                  data-testid={`compare-pick-${ev.id}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="font-medium text-gray-900">{ev.name}</div>
                    <div className="flex items-center gap-1.5">
                      {ev.is_archived && <span className="text-xs text-gray-500 px-1.5 py-0.5 rounded border border-gray-300">Archived</span>}
                      <span className={`w-5 h-5 rounded-full border flex items-center justify-center ${selected.includes(ev.id) ? 'bg-pink-500 border-pink-500' : 'border-gray-300'}`}>
                        {selected.includes(ev.id) && <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" /></svg>}
                      </span>
                    </div>
                  </div>
                  {ev.description && <div className="text-xs text-gray-500 mt-1 line-clamp-1">{ev.description}</div>}
                </button>
              ))}
            </div>
          </div>
        )}

        {step === 'pick' && (
          <div className="flex justify-between items-center pt-3 border-t">
            <span className="text-sm text-gray-500" data-testid="compare-pick-count">{selected.length} of 2–4 selected</span>
            <Button
              className="bg-pink-600 hover:bg-pink-700 text-white"
              disabled={selected.length < 2 || loading}
              onClick={runCompare}
              data-testid="compare-run-btn"
            >
              {loading ? 'Comparing…' : 'Compare'}
            </Button>
          </div>
        )}

        {step === 'view' && result && (
          <div className="flex-1 overflow-auto space-y-4">
            {/* Side-by-side metric grid */}
            <div
              className="grid gap-3"
              style={{ gridTemplateColumns: `repeat(${result.events.length}, minmax(0, 1fr))` }}
              data-testid="compare-grid"
            >
              {result.events.map(ev => (
                <div key={ev.id} className="border rounded-lg p-4 bg-white" data-testid={`compare-col-${ev.id}`}>
                  <h3 className="text-base font-semibold text-gray-900 truncate flex items-center gap-1.5" title={ev.name}>
                    {ev.name}
                    {ev.is_archived && <span className="text-[10px] font-normal text-gray-500 px-1.5 py-0.5 rounded border border-gray-300">Archived</span>}
                  </h3>

                  <Metric label="Orders"   value={fmt(ev.order_count)}    winner={isWinner(ev.id, 'orders')} />
                  <Metric label="Total Qty" value={fmt(ev.total_qty)}      winner={isWinner(ev.id, 'qty')} />
                  <Metric label="Value"    value={`₹${fmt(Math.round(ev.total_amount))}`} winner={isWinner(ev.id, 'amount')} />
                  <Metric label="Customers" value={fmt(ev.customer_count)} winner={isWinner(ev.id, 'customers')} />

                  <div className="mt-3 pt-3 border-t">
                    <div className="text-[11px] text-gray-500 uppercase tracking-wide font-semibold mb-1">Top Designs</div>
                    {ev.top_designs.length === 0 && <p className="text-xs text-gray-400">—</p>}
                    {ev.top_designs.map((d, i) => {
                      const isCommon = result.common_top_designs.includes(d.design_code);
                      return (
                        <div key={d.design_code} className="flex justify-between text-xs py-0.5">
                          <span className={isCommon ? 'font-semibold text-pink-700' : 'text-gray-700'}>
                            <span className="text-gray-400 mr-1">{i + 1}.</span>
                            {d.design_code}
                            {isCommon && <Sparkles className="inline w-3 h-3 ml-1 text-pink-500" />}
                          </span>
                          <span className="font-semibold text-gray-900">{d.qty}</span>
                        </div>
                      );
                    })}
                  </div>
                  <div className="mt-3 pt-3 border-t">
                    <div className="text-[11px] text-gray-500 uppercase tracking-wide font-semibold mb-1">Top Customers</div>
                    {ev.top_customers.length === 0 && <p className="text-xs text-gray-400">—</p>}
                    {ev.top_customers.map((c, i) => {
                      const isRepeat = result.common_customers.some(rc => rc.customer_id === c.customer_id);
                      return (
                        <div key={c.customer_id} className="flex justify-between text-xs py-0.5">
                          <span className={isRepeat ? 'font-semibold text-emerald-700' : 'text-gray-700 truncate'}>
                            <span className="text-gray-400 mr-1">{i + 1}.</span>
                            {c.customer_name}
                            {isRepeat && <Users className="inline w-3 h-3 ml-1 text-emerald-500" />}
                          </span>
                          <span className="font-semibold text-gray-900 ml-2">{c.qty}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>

            {/* Common ground panel */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="compare-common">
              <div className="border border-emerald-200 bg-emerald-50/50 rounded-lg p-4">
                <div className="text-sm font-semibold text-emerald-800 mb-2 flex items-center gap-1.5">
                  <Users className="w-4 h-4" /> Repeat Customers (across all selected)
                </div>
                {result.common_customers.length === 0
                  ? <p className="text-xs text-gray-500">No customers bought in all the selected events.</p>
                  : (
                    <ul className="text-xs space-y-0.5">
                      {result.common_customers.map(c => <li key={c.customer_id} className="text-gray-800">• {c.customer_name}</li>)}
                    </ul>
                  )}
              </div>
              <div className="border border-pink-200 bg-pink-50/50 rounded-lg p-4">
                <div className="text-sm font-semibold text-pink-800 mb-2 flex items-center gap-1.5">
                  <Award className="w-4 h-4" /> Common Top Designs
                </div>
                {result.common_top_designs.length === 0
                  ? <p className="text-xs text-gray-500">No designs ranked in the top 5 of all selected events.</p>
                  : (
                    <div className="flex flex-wrap gap-1.5">
                      {result.common_top_designs.map(dc => (
                        <span key={dc} className="text-xs font-medium bg-white border border-pink-300 text-pink-700 px-2 py-0.5 rounded">{dc}</span>
                      ))}
                    </div>
                  )}
              </div>
            </div>
          </div>
        )}

        {step === 'view' && (
          <div className="flex justify-end gap-2 pt-3 border-t">
            <Button variant="outline" onClick={() => { setStep('pick'); setResult(null); }} data-testid="compare-back-btn">Pick different events</Button>
            <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="compare-close-btn">Close</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const Metric = ({ label, value, winner }) => (
  <div className={`mt-2 flex justify-between items-center px-3 py-2 rounded ${winner ? 'bg-amber-50 border border-amber-200' : ''}`}>
    <span className="text-xs text-gray-500">{label}</span>
    <span className="text-lg font-bold text-gray-900 flex items-center gap-1.5">
      {value}
      {winner && <Trophy className="w-4 h-4 text-amber-500" />}
    </span>
  </div>
);

export default EventComparisonDialog;
